import React, { useCallback, useEffect, useMemo } from 'react';
import {
  GenericFormContainer,
  Loading,
  WIDGETS_TYPE,
  publishEvent
} from '@uma/react-commons/dist/index';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { FormTypesEnum } from 'src/utils/enums';
import { SecurityDetailsScreenDisplayed } from './analytics';
import { FeatureFlagProps } from 'src/App';
import { Center } from 'src/styles/styles';

const SecurityDetails = (featureFlagProps: FeatureFlagProps) => {
  const { translate, northboundAPIs } = useDependencyManagerContext();
  const navigation = northboundAPIs?.v1?.navigation;
  const country = northboundAPIs?.v1?.localization.country;
  const language = northboundAPIs?.v1?.localization.language;

  const path =
    (navigation.location.pathname.split('/').pop() as FormTypesEnum) || '';

  const {
    isLoading,
    securityHPIDEmail: isSecurityHpIdEmailEnabled,
    securityHPIDPassword: isSecurityHpIdPasswordEnabled,
    securityHPIDPhone: isSecurityHpIdPhoneEnabled,
    securityHPIDMfa: isSecurityHpIdMfaEnabled,
    securityLoginMethodsFieldEnabled: isSecurityLoginMethodsFieldEnabled
  } = featureFlagProps;

  const pathToRedirect = useMemo(
    () => `/${country}/${language}/security`,
    [country, language]
  );

  const handleRedirect = useCallback(() => {
    navigation?.push(pathToRedirect);
  }, [navigation, pathToRedirect]);

  useEffect(() => {
    if (!isLoading) {
      const formTypes = [] as FormTypesEnum[];

      isSecurityHpIdEmailEnabled && formTypes.push(FormTypesEnum.Email);
      isSecurityHpIdPhoneEnabled && formTypes.push(FormTypesEnum.Phone);
      isSecurityLoginMethodsFieldEnabled &&
        formTypes.push(FormTypesEnum.UserName);
      isSecurityHpIdPasswordEnabled && formTypes.push(FormTypesEnum.Password);
      isSecurityHpIdMfaEnabled && formTypes.push(FormTypesEnum.Mfa);

      if (path !== null && formTypes.includes(path as FormTypesEnum)) {
        publishEvent({
          ...SecurityDetailsScreenDisplayed,
          actionAuxParams: `type=${path}`
        });
        return;
      }

      handleRedirect();
    }
  }, [
    isSecurityHpIdEmailEnabled,
    isSecurityHpIdPasswordEnabled,
    isSecurityHpIdPhoneEnabled,
    isSecurityHpIdMfaEnabled,
    handleRedirect,
    path,
    isLoading,
    isSecurityLoginMethodsFieldEnabled
  ]);

  const widgets: WIDGETS_TYPE[] = [];

  switch (path) {
    case FormTypesEnum.Email:
      widgets.push(WIDGETS_TYPE.CHANGE_EMAIL);
      break;
    case FormTypesEnum.Phone:
      widgets.push(WIDGETS_TYPE.PHONE);
      break;
    case FormTypesEnum.Password:
      widgets.push(WIDGETS_TYPE.CHANGE_PASSWORD);
      break;
    case FormTypesEnum.UserName:
      widgets.push(WIDGETS_TYPE.USERNAME);
      break;
    case FormTypesEnum.Mfa:
      widgets.push(WIDGETS_TYPE.MFA);
      break;
    default:
      break;
  }

  if (isLoading) {
    return (
      <Center>
        <Loading testid="loading-security-details" />
      </Center>
    );
  }

  return (
    <div
      data-testid="security-details-page"
      aria-label="SecurityDetailsPage"
    >
      <GenericFormContainer
        data-testid="generic-form-container"
        title={translate('security.loginDetails', 'Login details')}
        language={`${language}_${country.toUpperCase()}`}
        showButtons={false}
        widgets={widgets}
        labels={{
          cancel: translate('common.cancel', 'Cancel'),
          save: translate('common.save', 'Save'),
          goBack: translate('common.goBack', 'Go back')
        }}
        onClickArrowLeft={handleRedirect}
      />
    </div>
  );
};

export default SecurityDetails;
